import { useState } from 'react';
import { OzetSerit } from '../components/satis-analiz/OzetSerit';
import { ZamanBlok } from '../components/satis-analiz/ZamanBlok';
import { KanalBlok } from '../components/satis-analiz/KanalBlok';
import { SubeBlok } from '../components/satis-analiz/SubeBlok';
import { KategoriBlok } from '../components/satis-analiz/KategoriBlok';
import { UyarilarBlok } from '../components/satis-analiz/UyarilarBlok';
import { CokSatanBlok } from '../components/satis-analiz/CokSatanBlok';
import { TiklaAc } from '../components/satis-analiz/TiklaAc';
import { IsiHaritasiBlok } from '../components/satis-analiz/IsiHaritasiBlok';
import { SepetBlok } from '../components/satis-analiz/SepetBlok';
import { StokSatisBlok } from '../components/satis-analiz/StokSatisBlok';
import { KarKoprusuBlok } from '../components/satis-analiz/KarKoprusuBlok';
import { FiltreSeridi } from '../components/satis-analiz/FiltreSeridi';
import { useSubeler } from '../components/satis-analiz/useSubeler';
import { useSatisAnalizFiltre } from '../components/satis-analiz/useSatisAnalizFiltre';
import { useSatisKaynaklari } from '../components/satis-analiz/ekranKaynaklari';
import { FiltreBarPortal, useFiltreBarYuvasi } from '../components/FiltreBarYuvasi';
import { Bilgi } from '../components/satis-analiz/Bilgi';
import { HUCRE } from '../components/satis-analiz/yerlesim';
import { DetayModal } from '../components/satis-analiz/detay/DetayModal';
import type { SaDetay } from '../components/satis-analiz/detay/detayTipleri';
import type { PanelScreen, ReportConfig } from '../types/panelConfig';

interface SatisAnalizProps {
  screen: PanelScreen;
  reports: ReportConfig[];
}

/**
 * Satis Analiz — S256 tek ekran (G2), G2b duzeni. Yerlesim SABIT; `config.bloklar`'a bagli DEGIL. Filtre durumu
 * URL'de. Ilk acilis 7 istek: 990040 x2 + 990051-990055 (spec §4.4); tikla-ac bloklari acilana kadar sorgu atmaz.
 * G2b: sayfa basligi yok (kirinti navbar'da); filtreler header'in hemen altindaki filtre barinda (Yerlice gibi).
 * K7 (G2b): tiklanan kategori/kanal/sube/gun/uyari/urun ortada DetayModal acar; modal acilana kadar detay sorgusu yok.
 */
export function SatisAnaliz({ screen, reports }: SatisAnalizProps) {
  const filtre = useSatisAnalizFiltre();
  const { donem, oncekiDonem } = filtre;
  const kaynaklar = useSatisKaynaklari(screen, reports);
  const { subeler, yuklendi } = useSubeler(kaynaklar.kanal040, donem);
  const { hedef } = useFiltreBarYuvasi();

  // URL'deki sube listede yoksa (o pencerede satis yok / kapanmis) TUM subelere donulur; liste
  // gelmeden sube gecersiz sayilmaz.
  const sube =
    filtre.sube === null ? null : !yuklendi || subeler.includes(filtre.sube) ? filtre.sube : null;
  const kanal = filtre.kanal;

  // K7: modal state SAYFADA (hucrede degil). Bloklar yalniz "hangi detay" bildirir.
  const [detay, setDetay] = useState<SaDetay | null>(null);

  const serit = <FiltreSeridi filtre={filtre} subeler={subeler} sube={sube} kanal={kanal} donem={donem} />;

  return (
    <div className="flex flex-col gap-4">
      {hedef !== null ? (
        // Header'in hemen altinda tam genislik bar (kullanici 2026-09-15, Yerlice `.filtre-bar`: yuzey zemin, alt
        // kenarlik, hafif golge, ~0.7rem dikey dolgu). Ic hiza Layout'un icerik kabiyla ayni: max-w-[1400px],
        // < 768 px p-4, ustu p-6 (useBreakpoint).
        <FiltreBarPortal>
          <div data-testid="filtre-bar" className="bg-surface border-b border-border shadow-[0_1px_3px_rgb(16_33_92/0.06)]">
            <div className="mx-auto w-full max-w-[1400px] px-4 md:px-6 py-2.5">{serit}</div>
          </div>
        </FiltreBarPortal>
      ) : (
        // Yuvasiz kabuk: sayfa icinde yapisik serit. `bg-page` SART: `bg-surface2` derlenmis CSS'te yok, serit seffaf
        // kalir ve kartlar altindan gecerken gorunurdu.
        <div data-testid="filtre-seridi-dar" className="sticky top-0 z-10 bg-page py-2">
          {serit}
        </div>
      )}

      {/* Satir 1 — dort kart (telefonda tek kolon) */}
      <OzetSerit kaynaklar={kaynaklar} donem={donem} oncekiDonem={oncekiDonem} sube={sube} kanal={kanal} />

      {/* Satir 2..4 — satirda en fazla IKI genis kart, esit boy (K4). `lg` altinda tek kolon. */}
      <div data-testid="satir-2" className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className={HUCRE}>
          <ZamanBlok
            kaynaklar={kaynaklar}
            donem={donem}
            donemTuru={filtre.donemTuru}
            sube={sube}
            kanal={kanal}
            onGunSec={(gun) => setDetay({ tur: 'gun', gun })}
          />
        </div>
        <div className={HUCRE}>
          <KanalBlok
            kaynak={kaynaklar.kanal040}
            donem={donem}
            oncekiDonem={oncekiDonem}
            sube={sube}
            kanal={kanal}
            onKanalSec={(k) => setDetay({ tur: 'kanal', kanal: k })}
          />
        </div>
      </div>

      <div data-testid="satir-3" className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className={HUCRE}>
          <SubeBlok
            kaynak={kaynaklar.kanal040}
            donem={donem}
            sube={sube}
            kanal={kanal}
            onSubeSec={(s) => setDetay({ tur: 'sube', sube: s })}
          />
        </div>
        <div className={HUCRE}>
          <UyarilarBlok
            kaynaklar={kaynaklar}
            donem={donem}
            oncekiDonem={oncekiDonem}
            sube={sube}
            kanal={kanal}
            onUyariSec={setDetay}
          />
        </div>
      </div>

      <div data-testid="satir-4" className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className={HUCRE}>
          <KategoriBlok
            kaynak={kaynaklar.kategori}
            donem={donem}
            oncekiDonem={oncekiDonem}
            sube={sube}
            kanal={kanal}
            onKategoriSec={(k) => setDetay({ tur: 'kategori', kategori: k })}
            onDigerSec={() => setDetay({ tur: 'digerKategoriler' })}
          />
        </div>
        <div className={HUCRE}>
          <CokSatanBlok
            kaynak={kaynaklar.enCokSatan}
            donem={donem}
            sube={sube}
            kanal={kanal}
            onUrunSec={(u) => setDetay({ tur: 'urun', stokKodu: u.stokKodu, urunAdi: u.urunAdi })}
          />
        </div>
      </div>

      {/* Tikla-ac — TEK cerceveli liste, dort satir (spec §7.2). Kapali baslar, acilana kadar sorgu atmaz. Kaynak
          yoksa kabuk bile cizilmez; hicbiri yoksa `empty:hidden` bos cerceve birakmaz. Kapsam notlari
          ("kanal süzgeci uygulanmaz" vb.) alt basliktan DUSMEZ (controller ruling S3, spec §7.3) — basligin
          yanindaki Bilgi ⓘ'ye tasinir. */}
      <div
        data-testid="tikla-ac-bloklar"
        className="flex flex-col bg-surface border border-border rounded-lg divide-y divide-border empty:hidden"
      >
        {kaynaklar.isi042 && (
          <TiklaAc anahtar="isi" baslik="Saat × gün yoğunluğu" altBaslik="Hangi gün, hangi saatte satış yoğun">
            <IsiHaritasiBlok kaynak={kaynaklar.isi042} donem={donem} sube={sube} kanal={kanal} />
          </TiklaAc>
        )}
        {kaynaklar.sepet019 && (
          <TiklaAc
            anahtar="sepet"
            baslik="Birlikte satılanlar"
            altBaslik="Son 90 gün"
            // Controller ruling S3 fix1: not YALNIZ kanal seciliyken alakalı — secili degilken ⓘ hic basilmaz.
            bilgi={kanal !== null && <Bilgi testid="bilgi-tikla-ac-sepet" metin="kanal süzgeci uygulanmaz" />}
          >
            <SepetBlok reports={reports} sube={sube} kanal={kanal} />
          </TiklaAc>
        )}
        {kaynaklar.cokSatan014 && kaynaklar.kritik025 && (
          <TiklaAc
            anahtar="stok"
            baslik="Stok × satış"
            altBaslik="Satıyor ama stok bitiyor"
            bilgi={kanal !== null && <Bilgi testid="bilgi-tikla-ac-stok" metin="kanal süzgeci uygulanmaz" />}
          >
            <StokSatisBlok kaynaklar={kaynaklar} donem={donem} sube={sube} kanal={kanal} />
          </TiklaAc>
        )}
        {kaynaklar.urunKarliligi && (
          <TiklaAc anahtar="kopru" baslik="Kâr köprüsü" altBaslik="Ciro → maliyet → brüt kâr">
            <KarKoprusuBlok kaynaklar={kaynaklar} donem={donem} sube={sube} kanal={kanal} />
          </TiklaAc>
        )}
      </div>

      <DetayModal
        detay={detay}
        onKapat={() => setDetay(null)}
        kaynaklar={kaynaklar}
        donem={donem}
        oncekiDonem={oncekiDonem}
        sube={sube}
        kanal={kanal}
      />
    </div>
  );
}
